import { useState } from "react";

type ExtractType = "text" | "image" | "video" | "link" | "source";

const typeOptions: { key: ExtractType; label: string; icon: string }[] = [
  { key: "text", label: "文字提取", icon: "ri-file-text-line" },
  { key: "image", label: "图片提取", icon: "ri-image-2-line" },
  { key: "video", label: "视频解析", icon: "ri-play-circle-line" },
  { key: "link", label: "链接提取", icon: "ri-link-m" },
  { key: "source", label: "源码查看", icon: "ri-code-s-slash-line" },
];

const samples: Record<ExtractType, string> = {
  text: "【正文预览】某平台博主发文称…，事件持续发酵。\n\n第 1 段：当事人回应此前传闻，称相关截图并不完整。\n第 2 段：多位网友晒出对比资料，提出不同时间线。\n第 3 段：官方渠道暂未进一步说明，评论区持续热议。\n\n（以上为提取结果的正文节选，完整文本已就绪可复制。）",
  image: "共提取到 12 张图片素材：\n\n1. hero-cover.jpg    1920 × 1080\n2. scene-01.png      1600 × 900\n3. detail-02.jpg     1200 × 800\n4. avatar-03.png     512 × 512\n…（其余 8 张已按序排列，含高清原图与直链）",
  video: "视频解析结果：\n\n主播放源：MP4 · 1080P\n备用播放源：MP4 · 720P\n音频来源：AAC · 128Kbps\n\n已为你生成可直接复制或保存的真实播放地址。",
  link: "共识别到 18 条链接：\n\n· 正文内链 × 7\n· 图片外链 × 5\n· 站内跳转 × 4\n· 外部来源 × 2\n\n已按类型分组，可直接一键复制完整清单。",
  source: "关键结构预览：\n\n<div class=\"post-content\">\n  <h1>…正文标题…</h1>\n  <p>…段落内容…</p>\n  <img src=\"…原图地址…\" />\n</div>\n\nHTML 结构与图片、脚本地址已定位，可在源码模式下查看完整层级。",
};

const done = {
  text: "提取完成：已生成 2,360 字正文，并过滤广告与导航噪音。",
  image: "提取完成：已汇总全部可见图片及其尺寸信息。",
  video: "解析完成：已定位主播放源与清晰度信息。",
  link: "提取完成：已分组整理页面全部链接。",
  source: "解析完成：已定位页面关键结构节点。",
};

export default function Demo() {
  const [type, setType] = useState<ExtractType>("text");
  const [url, setUrl] = useState("https://example.com/post/12345");
  const [started, setStarted] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = () => {
    if (!url.trim()) return;
    setParsing(true);
    setStarted(false);
    setCopied(false);
    window.setTimeout(() => {
      setParsing(false);
      setStarted(true);
    }, 900);
  };

  const copyText = async () => {
    const target = samples[type];
    try {
      await navigator.clipboard.writeText(target);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = target;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section id="demo" className="section-pad scroll-mt-20 bg-background-100">
      <div className="container-x">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="text-sm font-medium tracking-wide text-primary-600">快速体验</p>
            <h2 className="mt-2 font-heading text-3xl text-foreground-900 md:text-4xl">贴个链接，即刻开始</h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
              吃瓜网页没有繁琐的注册流程。把想要处理的网页地址粘贴进来，选好提取类型，剩下的交给解析引擎。
            </p>

            <div className="mt-6 space-y-4">
              {[
                { n: "01", t: "粘贴链接", d: "把公众号、帖子或新闻页地址复制进来" },
                { n: "02", t: "选择提取类型", d: "文字 / 图片 / 视频 / 链接 / 源码，任你挑选" },
                { n: "03", t: "复制保存结果", d: "提取结果秒级呈现，一键复制到剪贴板" },
              ].map((s) => (
                <div key={s.n} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-sm text-background-50">
                    {s.n}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{s.t}</p>
                    <p className="text-xs text-foreground-600 md:text-sm">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-200 bg-background-100 px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-primary-400" />
              <span className="h-3 w-3 rounded-full bg-secondary-400" />
              <span className="h-3 w-3 rounded-full bg-accent-400" />
              <span className="ml-3 flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-lock-2-line" aria-hidden="true" /> 吃瓜网页 提取控制台
              </span>
            </div>

            <div className="p-5">
              <div className="flex items-center gap-2 rounded-xl border border-background-200 bg-background-100 px-3 py-2.5">
                <i className="ri-link text-primary-600" aria-hidden="true" />
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="粘贴要提取的网页链接…"
                  aria-label="要提取的网页链接"
                  className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {typeOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      setType(o.key);
                      setStarted(false);
                    }}
                    aria-pressed={type === o.key}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      type === o.key
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    <i className={o.icon} aria-hidden="true" />
                    {o.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={run}
                className="mt-4 w-full rounded-xl bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {parsing ? "正在解析页面…" : "开始提取"}
              </button>

              <div className="mt-4 min-h-[190px] rounded-xl border border-dashed border-background-300 bg-background-100/60 p-4">
                {parsing ? (
                  <div className="flex h-full min-h-[160px] flex-col items-center justify-center gap-2 text-foreground-500">
                    <i className="ri-loader-4-line animate-spin text-2xl text-primary-600" aria-hidden="true" />
                    <p className="text-xs">正在抓取页面结构，请稍候…</p>
                  </div>
                ) : started ? (
                  <>
                    <p className="mb-2 flex items-center gap-2 text-xs font-bold text-accent-700">
                      <i className="ri-checkbox-circle-line text-sm" aria-hidden="true" />
                      {done[type]}
                    </p>
                    <pre className="whitespace-pre-wrap break-words rounded-lg bg-background-50 p-3 font-sans text-xs leading-relaxed text-foreground-700">
                      {samples[type]}
                    </pre>
                  </>
                ) : (
                  <div className="flex h-full min-h-[160px] items-center justify-center text-center text-xs text-foreground-400">
                    选择提取类型后点击「开始提取」，
                    <br />
                    这里会展示示例结果
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={copyText}
                disabled={!started}
                className={`mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors ${
                  started
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "cursor-not-allowed bg-background-200 text-foreground-400"
                }`}
              >
                <i className={`${copied ? "ri-check-line" : "ri-file-copy-line"}`} aria-hidden="true" />
                {copied ? "已复制到剪贴板" : "复制结果"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}